// react
import { createRef, useCallback, useMemo } from 'react';
// components
import { Image, MarketplaceCard, StoryDrivenUserGroup } from '@/components/common';
// @mui
import {
  Typography,
  Container,
  Grid,
  Divider,
  Rating,
  Stack,
  Link as MuiLink,
} from '@mui/material';
// react-slick
import Slider from 'react-slick';
// hooks
import { useShared } from '@/hooks';
// images
import { defaultInvestor } from '@/images';

export default function AboutUserGroup({ userGroupPageDetail, userGroupType }) {
  const { countries } = useShared('countries');

  const {
    title,
    bannerPhoto,
    userGroupMembers,
    industryInterests,
    description,
    social,
    sme = {},
    ratingScore: _ratingScore,
  } = userGroupPageDetail;

  const ratingScore = useMemo(() => Number(_ratingScore), [_ratingScore]);

  const handleShowRatings = useCallback(() => {
    const sectionElement = document.getElementById('ratings');
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const startupdashSlider = createRef();
  const startupplacesetting = useMemo(
    () => ({
      dots: true,
      slidesToShow: 1,
      arrows: true,
      responsive: [
        {
          breakpoint: 800,
          settings: { slidesToShow: 1, slidesToScroll: 1, infinite: true },
        },
      ],
    }),
    []
  );

  return (
    <>
      <Container sx={{ bgcolor: '#F7F7F7' }} maxWidth="100%">
        <Stack direction="column" alignItems="center" justifyContent="center" my={1}>
          <Typography variant="h2">{title}</Typography>
          {Boolean(ratingScore) && (
            <MuiLink color="black" onClick={handleShowRatings} component="button">
              <Stack direction="row" alignItems="center" spacing={1} mx="auto">
                <Typography variant="h4">{ratingScore}</Typography>
                <Rating readOnly value={ratingScore} precision={0.1} />
              </Stack>
            </MuiLink>
          )}
        </Stack>
        <Container maxWidth="lg">
          <Image
            imageClass={'investorpagebuilderimage1'}
            src={bannerPhoto}
            ratio="32/5"
            quality={100}
            onError={function (e) {
              e.target.src = defaultInvestor;
              e.target.srcset = defaultInvestor.src;
            }}
            cardClass={'investorpagebuilderimage1'}
            type={'banner'}
            alt={title}
          />
        </Container>
      </Container>
      <Container>
        <Typography className="startupdashboardtitle2">{title} Team</Typography>
        <div className="startupmarketmobile">
          <Container className="startupmarketplace" maxWidth="md">
            <Slider ref={startupdashSlider} {...startupplacesetting}>
              {userGroupMembers?.map((card) => {
                return (
                  <MarketplaceCard
                    link={`/users/${card.userId}`}
                    key={'mobil' + card.id}
                    title={''}
                    image={card.user.detail.profilePhoto}
                    name={card.user.detail.firstName + ' ' + card.user.detail.lastName}
                    profession={card.position}
                    location={card.user.detail.country}
                    description={card.user.detail.description}
                    countries={countries}
                    locationArea={card.user.detail.locationArea}
                  />
                );
              })}
            </Slider>
          </Container>
        </div>
        <div className="startupmarketweb">
          <Container className="startupmarketplace">
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ margin: '1rem', overflow: 'auto' }}
            >
              {userGroupMembers?.map((card) => {
                if (card.level !== 1) {
                  return;
                }
                return (
                  <MarketplaceCard
                    link={`/users/${card.userId}`}
                    key={'web' + card.id}
                    title={''}
                    image={card.user.detail.profilePhoto}
                    name={card.user.detail.firstName + ' ' + card.user.detail.lastName}
                    profession={card.position}
                    location={card.user.detail.country}
                    description={card.user.detail.shortBio}
                    countries={countries}
                    locationArea={card.user.detail.locationArea}
                  />
                );
              })}
            </Grid>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ margin: '1rem', overflow: 'auto' }}
            >
              {userGroupMembers?.map((card) => {
                if (card.level !== 2) {
                  return;
                }
                return (
                  <MarketplaceCard
                    link={`/users/${card.userId}`}
                    key={'web' + card.id}
                    title={''}
                    image={card.user.detail.profilePhoto}
                    name={card.user.detail.firstName + ' ' + card.user.detail.lastName}
                    profession={card.position}
                    location={card.user.detail.country}
                    description={card.user.detail.shortBio}
                    countries={countries}
                    locationArea={card.user.detail.locationArea}
                  />
                );
              })}
            </Grid>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ margin: '1rem', overflow: 'auto' }}
            >
              {userGroupMembers?.map((card) => {
                if (card.level !== 3) {
                  return;
                }
                return (
                  <MarketplaceCard
                    link={`/users/${card.userId}`}
                    key={'web' + card.id}
                    title={''}
                    image={card.user.detail.profilePhoto}
                    name={card.user.detail.firstName + ' ' + card.user.detail.lastName}
                    profession={card.position}
                    location={card.user.detail.country}
                    description={card.user.detail.shortBio}
                    countries={countries}
                    locationArea={card.user.detail.locationArea}
                  />
                );
              })}
            </Grid>
          </Container>
        </div>
      </Container>
      <Divider sx={{ mb: 4 }} />
      <Container maxwidth="lg">
        <StoryDrivenUserGroup
          title={`About ${title}`}
          interests={industryInterests}
          categories={sme?.smeCategories}
          description={description}
          social={social}
          userGroupType={userGroupType}
        />
      </Container>
    </>
  );
}
